import React from 'react';
import { User, Building } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ClientTypeBadgeProps {
  documentType: 'cpf' | 'cnpj' | null | undefined;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
}

const CLIENT_TYPE_CONFIG = {
  cpf: {
    label: 'PF', // Pessoa Física
    className: 'bg-blue-dark-100 text-blue-dark-600 border-blue-dark-200',
    icon: User,
    ariaLabel: 'Pessoa Física',
  },
  cnpj: {
    label: 'PJ', // Pessoa Jurídica
    className: 'bg-blue-medium-100 text-blue-medium-700 border-blue-medium-200',
    icon: Building,
    ariaLabel: 'Pessoa Jurídica',
  },
} as const;

const SIZE_CONFIG = {
  sm: {
    badge: 'h-5 w-5',
    icon: 'h-3 w-3',
    text: 'text-xs',
  },
  md: {
    badge: 'h-6 w-6',
    icon: 'h-4 w-4',
    text: 'text-sm',
  },
  lg: {
    badge: 'h-7 w-7',
    icon: 'h-5 w-5',
    text: 'text-base',
  },
} as const;

export function ClientTypeBadge({
  documentType,
  size = 'md',
  showLabel = false,
  className,
}: ClientTypeBadgeProps) {
  // Return null for null/undefined document types
  if (!documentType) {
    return null;
  }

  const config = CLIENT_TYPE_CONFIG[documentType];
  const sizeConfig = SIZE_CONFIG[size];

  const Icon = config.icon;

  return (
    <div
      className={cn(
        // Badge container with Bauhaus styling
        'inline-flex items-center justify-center',
        'rounded-lg border-1',
        'transition-all duration-200',
        'shrink-0',

        // Size-specific styling
        sizeConfig.badge,

        // Type-specific colors
        config.className,

        // Hover effects
        'hover:shadow-sm',

        // Custom classes
        className
      )}
      aria-label={config.ariaLabel}
    >
      <Icon
        className={cn(
          sizeConfig.icon,
          'flex-shrink-0'
        )}
      />
      {showLabel && (
        <span
          className={cn(
            'ml-1 font-semibold tracking-wide',
            sizeConfig.text
          )}
        >
          {config.label}
        </span>
      )}
    </div>
  );
}

export default ClientTypeBadge;